import type { ApiService } from "../../services/apiService";
export const collectionPath = "/mobile/collections";
export const colors = [
  "#FF6B35", "#4F46E5", "#10B981", "#F59E0B", "#EC4899", "#3B82F6",
];
export const colorNames = ["Orange", "Indigo", "Emerald", "Amber", "Pink", "Blue"];
export function collectionColor(value: string | null) {
  const named = colorNames.findIndex(name => name.toLowerCase() === value?.toLowerCase());
  return named >= 0 ? colors[named] : /^#[0-9a-f]{6}$/i.test(value ?? "") ? value! : "#e0d6c9";
}
export interface Collection {
  id: string;
  name: string;
  colorLabel: string | null;
  description?: string | null;
  itemCount: number;
}
export interface CollectionDetail extends Collection {
  items: CollectionProduct[];
}
export interface CollectionProduct {
  collectionMembershipId?: string;
  entityId: number;
  itemName: string;
  itemNumber: string;
  description?: string | null;
  imageReference?: string | null;
}
export interface CollectionChanges {
  details?: { name: string; description: string | null; colorLabel: string | null };
  addedProductEntityIds: number[];
  removedProductEntityIds: number[];
  removedMembershipIds?: string[];
}
export function collectionChanges(saved: CollectionDetail, draft: CollectionDetail): CollectionChanges {
  const result: CollectionChanges = {
    addedProductEntityIds: draft.items.filter(item => !saved.items.some(old => old.entityId === item.entityId)).map(item => item.entityId),
    removedProductEntityIds: saved.items.filter(item => !draft.items.some(next => next === item || (item.collectionMembershipId ? next.collectionMembershipId === item.collectionMembershipId : next.entityId === item.entityId)) && item.entityId > 0).map(item => item.entityId),
  };
  const removedMembershipIds = saved.items.filter(item => item.entityId <= 0 && item.collectionMembershipId && !draft.items.some(next => next.collectionMembershipId === item.collectionMembershipId)).map(item => item.collectionMembershipId!);
  if (removedMembershipIds.length) result.removedMembershipIds = removedMembershipIds;
  if (saved.name !== draft.name || (saved.description ?? "") !== (draft.description ?? "") || saved.colorLabel !== draft.colorLabel)
    result.details = { name: draft.name.trim(), description: draft.description?.trim() || null, colorLabel: draft.colorLabel };
  return result;
}
export const COLLECTION_NAVIGATION_EVENT = "collection-before-navigation";
export function requestCollectionNavigation(continueNavigation: () => void) {
  const event = new CustomEvent(COLLECTION_NAVIGATION_EVENT, { cancelable: true, detail: continueNavigation });
  if (window.dispatchEvent(event)) continueNavigation();
}

// Use the existing authenticated Elasticsearch proxy and its indexed product fields.
export async function searchCollectionProducts(api: ApiService, term: string): Promise<CollectionProduct[]> {
  const response = await api.post<{ hits: { hits: { _source: Record<string, unknown> }[] } }>("/product-search/query", {
    size: 10, _source: ["entityId", "ItemName", "ItemNumber", "ItemDescriptionSales", "ItemDescription", "primaryImageUrl"],
    query: { bool: { should: [
      { term: { ItemNumber: { value: term, boost: 12 } } },
      { prefix: { ItemNumber: { value: term, boost: 8 } } },
      { multi_match: { query: term, fields: ["ItemName^5", "ItemDescription.*^2", "ItemDescriptionSales.*^2"], fuzziness: "AUTO" } },
    ], minimum_should_match: 1 } },
  });
  const localized = (value: unknown): string => typeof value === "string" ? value
    : value && typeof value === "object" ? String((value as Record<string, unknown>).en ?? "") : "";
  return response.hits.hits.flatMap(({ _source: item }) => {
    const id = productEntityId(item.entityId);
    return id ? [{ entityId: id, itemName: localized(item.ItemName), itemNumber: localized(item.ItemNumber),
      description: localized(item.ItemDescriptionSales) || localized(item.ItemDescription), imageReference: localized(item.primaryImageUrl) }] : [];
  });
}
export interface AddResult {
  added: boolean;
  alreadyAdded: boolean;
  collection: CollectionDetail;
}
export function productEntityId(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}
export async function addToCollections(
  api: ApiService,
  entityId: number,
  ids: string[]
) {
  return Promise.all(
    ids.map(async (id) => {
      try {
        const result = await api.post<AddResult>(
          `${collectionPath}/${id}/products/${entityId}`,
          {}
        );
        return { id, status: result.alreadyAdded ? "present" : "added" };
      } catch {
        return { id, status: "failed" };
      }
    })
  );
}
